import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Button, Card, CardContent, CircularProgress, Stack, ToggleButton, ToggleButtonGroup,
    Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { cardTitleSx, monoFont, numberSx, tokens } from "../../theme";
import { loadAngleProfile } from "../../reducer/RawSurfaceSlice";

// Theta range drawn, degrees. A capture never reaches much past +/-90 (the surface curves out of
// the laser's view first), and fixing the axis rather than autoscaling to the data keeps the
// nipple's position comparable between two runs at different settings.
const THETA_FULL = 90;

// Zoomed span, degrees either side. The nipple is a few degrees wide - about 3% of the full
// axis - so at full span its flanks are a handful of pixels and the shape that decides whether
// the lobe is genuinely asymmetric simply isn't resolvable. This is the view that answers that.
const THETA_ZOOM = 15;
// Tightest window pan/zoom will produce - a couple of 0.5-degree bins, below which the chart
// has nothing left to say.
const MIN_SPAN_DEG = 1.5;

// The 2026-09-02 touchbase's proposed ±45° point-level window, drawn for reference.
//
// NOT the same thing as production's DefaultAngleLimit, which is also 45 - that is a MINIMUM TOTAL
// CENTRAL ANGLE below which a whole circle is rejected (i.e. ±22.5°), not a ±45° window. The two
// were conflated in the original planning doc; they are different gates that happen to share a
// number, and a circle spanning 85° passes the production gate comfortably while a ±45° window
// would barely touch it.
const PROPOSED_WINDOW_DEGREES = 45;

const CHART_HEIGHT = 300;
const PAD = { left: 56, right: 14, top: 12, bottom: 34 };

const labelSx = { fontSize: "11.5px", color: tokens.muted };

// Matches API's RawSurfaceFitter.EdgeLimitReason - bare int over the wire, same convention as
// CircleFixMode and common.js's DataFilterTypes.
const EDGE_REASON = {
    0: "no data",
    1: "field of view",
    2: "incidence",
    3: "profile end",
};

/**
 * Pooled signed-residual-vs-polar-angle profile for a whole capture: the diagnostic that measures
 * the nipple instead of assuming it.
 *
 * What is plotted: for every point on every fitted circle, the point's signed radial distance
 * from its own circle (positive = further from the centre = closer to the laser head) against its
 * polar angle about that circle's centre, with 0 deg being the direction of the laser. Millions of
 * points reduced to per-degree medians and a P10-P90 band. One circle's worth of this is noise;
 * pooled across thousands, a feature a few degrees wide becomes obvious.
 *
 * Why it exists: the nipple is a specular artefact, so it can only ever pull points TOWARD the
 * laser - it must show up as a positive-residual bump near 0 deg or it is not the nipple. Reading
 * that bump's centre and width off this chart is what turns the geofencing/clipping numbers into
 * measurements rather than guesses, and comparing the two series answers whether circle fixing
 * already removes it or merely absorbs it into a skewed fit.
 */
/**
 * `bare` drops the Card shell so this can be hosted in a dialog. The chart moved into a modal
 * because its x-axis is polar ANGLE while every chart it used to sit beneath is position along
 * the shaft - stacked in one column they read as sharing an x-axis, which they do not.
 */
export default function AngleProfileCard({ bare = false }) {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();
    const canvasRef = useRef(null);
    const [rangeChoice, setRangeChoice] = useState("all");
    const [hover, setHover] = useState(null);

    // The drawn theta window. Was derived from a boolean (+/-90 or +/-15); now it is state so
    // wheel/drag can move it continuously. The two buttons still set it, they are just no longer
    // the only thing that can.
    const [view, setView] = useState({ min: -THETA_FULL, max: THETA_FULL });
    const thetaMin = view.min;
    const thetaMax = view.max;
    // Decorations that only make sense close in (the measured peak) versus wide out (the +/-45
    // geofence). Was the zoom boolean; now a span test, since the window is continuous.
    const tight = thetaMax - thetaMin <= THETA_ZOOM * 2 + 1e-9;

    // Which preset button reads as active. Null while free-panned or free-zoomed, so neither
    // button claims to describe a window it is not describing.
    const preset = Math.abs(thetaMin + THETA_FULL) < 1e-9 && Math.abs(thetaMax - THETA_FULL) < 1e-9
        ? "full"
        : Math.abs(thetaMin + THETA_ZOOM) < 1e-9 && Math.abs(thetaMax - THETA_ZOOM) < 1e-9
            ? "zoom"
            : null;

    // Panning past the measured domain would show empty axis, and collapsing the span past a
    // fraction of a bin would show nothing at all.
    const clampView = useCallback((min, max) => {
        let span = Math.min(Math.max(max - min, MIN_SPAN_DEG), THETA_FULL * 2);
        let lo = min;
        if (lo < -THETA_FULL) lo = -THETA_FULL;
        if (lo + span > THETA_FULL) lo = THETA_FULL - span;
        return { min: lo, max: lo + span };
    }, []);

    const profile = rawSurface.angleProfile;
    const status = rawSurface.angleProfileStatus;

    const range = rangeChoice === "machine" ? rawSurface.machineRange
        : rangeChoice === "motor" ? rawSurface.motorRange
        : null;

    // The profile is computed against the circle-fix settings that were live when it ran, and
    // nothing re-runs it automatically (it is a full re-walk of the capture). Saying so is better
    // than either silently showing a stale "after fixing" curve or discarding the run.
    const stale = useMemo(() => {
        const at = profile?.requestedWith;
        if (!at) return false;
        return at.circleFixLimit !== rawSurface.circleFixLimit
            || at.circleFixMode !== rawSurface.circleFixMode
            || at.adaptiveK !== rawSurface.adaptiveK;
    }, [profile, rawSurface.circleFixLimit, rawSurface.circleFixMode, rawSurface.adaptiveK]);

    // Bins outside the drawn theta window are dropped here rather than in the API: the server's
    // full-range output is the honest record of what was measured, and clamping belongs to the
    // view that has a fixed axis.
    const series = useMemo(() => {
        const inWindow = (bins) => (bins ?? []).filter((b) => b.theta >= thetaMin && b.theta <= thetaMax);
        return { original: inWindow(profile?.original), fixed: inWindow(profile?.fixed) };
    }, [profile, thetaMin, thetaMax]);

    // Symmetric about zero so the sign of the feature is readable at a glance - an asymmetric
    // axis would make a positive bump look like a baseline shift. Driven by the P10/P90 band
    // rather than the medians so the band is never clipped by its own axis.
    const yMax = useMemo(() => {
        let max = 20;
        for (const b of series.original) {
            max = Math.max(max, Math.abs(b.p10), Math.abs(b.p90));
        }
        for (const b of series.fixed) {
            max = Math.max(max, Math.abs(b.p10), Math.abs(b.p90));
        }
        return max * 1.08;
    }, [series]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ratio = window.devicePixelRatio || 1;
        const width = canvas.clientWidth;
        const height = CHART_HEIGHT;
        canvas.width = width * ratio;
        canvas.height = height * ratio;

        const ctx = canvas.getContext("2d");
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, width, height);

        const plotW = width - PAD.left - PAD.right;
        const plotH = height - PAD.top - PAD.bottom;
        const sx = (theta) => PAD.left + ((theta - thetaMin) / (thetaMax - thetaMin)) * plotW;
        const sy = (residual) => PAD.top + plotH / 2 - (residual / yMax) * (plotH / 2);

        ctx.fillStyle = tokens.paper;
        ctx.fillRect(PAD.left, PAD.top, plotW, plotH);

        // Gridlines every 15 deg, labelled every 30.
        ctx.strokeStyle = tokens.line;
        ctx.fillStyle = tokens.muted2;
        ctx.font = `10px ${monoFont}`;
        ctx.textAlign = "center";
        ctx.lineWidth = 1;
        // Scales with the window instead of flipping between two fixed pairs, so a freely
        // zoomed view keeps a readable number of gridlines rather than 2 or 400.
        const [gridStep, labelEvery] = angleGrid(thetaMax - thetaMin);
        // Started from thetaMin before the window could be panned, which was fine while it was
        // always a round +/-90 or +/-15. Panned to an arbitrary angle that put gridlines on
        // values like -13.7 deg, so they are snapped to multiples of the step and the labels are
        // rounded to the step's own precision.
        const firstTick = Math.ceil(thetaMin / gridStep - 1e-9) * gridStep;
        const tickDecimals = gridStep < 1 ? 2 : gridStep < 5 ? 1 : 0;
        for (let i = 0; ; i++) {
            const theta = firstTick + i * gridStep;
            if (theta > thetaMax + 1e-9) break;
            const x = Math.round(sx(theta)) + 0.5;
            const isZero = Math.abs(theta) < 1e-9;
            ctx.globalAlpha = isZero ? 0 : 0.55;
            ctx.beginPath();
            ctx.moveTo(x, PAD.top);
            ctx.lineTo(x, PAD.top + plotH);
            ctx.stroke();
            if (Math.abs(theta % labelEvery) < 1e-9) {
                ctx.globalAlpha = 1;
                ctx.fillText(`${theta.toFixed(tickDecimals)}°`, x, PAD.top + plotH + 15);
            }
        }
        ctx.globalAlpha = 1;

        // Residual axis: zero emphasised, since "on the circle" is the reference everything here
        // is measured against.
        const yStep = niceStep(yMax);
        ctx.textAlign = "right";
        for (let r = -Math.floor(yMax / yStep) * yStep; r <= yMax; r += yStep) {
            const y = Math.round(sy(r)) + 0.5;
            ctx.strokeStyle = tokens.line;
            ctx.globalAlpha = r === 0 ? 1 : 0.5;
            ctx.beginPath();
            ctx.moveTo(PAD.left, y);
            ctx.lineTo(PAD.left + plotW, y);
            ctx.stroke();
            ctx.globalAlpha = 1;
            ctx.fillStyle = tokens.muted2;
            ctx.fillText(String(Math.round(r)), PAD.left - 7, y + 3.5);
        }

        // Everything outside the measured usable arc, dimmed. Deliberately a veil over the
        // rejected ends rather than a highlight on the good middle - it needs no new hue competing
        // with the two data series, and "past this line the data isn't worth using" is what the
        // measurement actually says.
        const arc = profile?.arc;
        const arcUsable = arc && arc.high > arc.low;
        if (arcUsable) {
            ctx.save();
            ctx.fillStyle = "rgba(22,20,21,0.62)";
            const ax0 = sx(Math.max(thetaMin, arc.low));
            const ax1 = sx(Math.min(thetaMax, arc.high));
            if (ax0 > PAD.left) ctx.fillRect(PAD.left, PAD.top, ax0 - PAD.left, plotH);
            if (ax1 < PAD.left + plotW) ctx.fillRect(ax1, PAD.top, PAD.left + plotW - ax1, plotH);
            ctx.restore();

            ctx.strokeStyle = tokens.muted;
            ctx.lineWidth = 1.5;
            [[ax0, arc.low], [ax1, arc.high]].forEach(([ax, theta]) => {
                if (theta <= thetaMin || theta >= thetaMax) return;
                ctx.beginPath();
                ctx.moveTo(Math.round(ax) + 0.5, PAD.top);
                ctx.lineTo(Math.round(ax) + 0.5, PAD.top + plotH);
                ctx.stroke();
            });
        }

        // The detector's measured clip window. Hatched rather than a flat fill so it reads as a
        // decision laid over the data rather than a third data series, and so it survives being
        // printed or read by someone who can't separate the hues.
        const win = profile?.window;
        if (win?.found) {
            const x0 = sx(Math.max(thetaMin, win.start));
            const x1 = sx(Math.min(thetaMax, win.stop));
            ctx.save();
            ctx.beginPath();
            ctx.rect(x0, PAD.top, x1 - x0, plotH);
            ctx.clip();
            ctx.fillStyle = "rgba(224,132,96,0.16)";
            ctx.fillRect(x0, PAD.top, x1 - x0, plotH);
            ctx.strokeStyle = "rgba(224,132,96,0.5)";
            ctx.lineWidth = 1;
            for (let d = -plotH; d < (x1 - x0) + plotH; d += 6) {
                ctx.beginPath();
                ctx.moveTo(x0 + d, PAD.top + plotH);
                ctx.lineTo(x0 + d + plotH, PAD.top);
                ctx.stroke();
            }
            ctx.restore();
            ctx.strokeStyle = tokens.reject;
            ctx.lineWidth = 1.5;
            [x0, x1].forEach((x) => {
                ctx.beginPath();
                ctx.moveTo(Math.round(x) + 0.5, PAD.top);
                ctx.lineTo(Math.round(x) + 0.5, PAD.top + plotH);
                ctx.stroke();
            });
        }

        // Both levels, because they do different jobs and confusing them is what produced a
        // one-degree window for a five-degree bump on the first real run. The upper line is the
        // significance test the PEAK has to clear for a bump to exist at all; the lower one is
        // where the outward walk stops, and so is what actually sets the window's edges. Drawn
        // even when no window was found - seeing how far the surface sits below the upper line is
        // the evidence for "no nipple".
        // Both labels sit at the left edge, and detect/edge are only a few µm apart whenever the
        // surface is quiet - which is most of the time - so they overprint into an unreadable
        // blob unless the second one is pushed clear. Track the last label's y and nudge.
        let lastLabelY = -Infinity;
        const level = (value, label, dash) => {
            if (!Number.isFinite(value)) return;
            ctx.save();
            ctx.setLineDash(dash);
            ctx.strokeStyle = tokens.muted;
            ctx.lineWidth = 1;
            const ly = Math.round(sy(value)) + 0.5;
            ctx.beginPath();
            ctx.moveTo(PAD.left, ly);
            ctx.lineTo(PAD.left + plotW, ly);
            ctx.stroke();
            ctx.restore();

            let textY = ly - 5;
            if (Math.abs(textY - lastLabelY) < 11) {
                textY = lastLabelY + 11;
            }
            lastLabelY = textY;

            ctx.fillStyle = tokens.muted;
            ctx.font = `10px ${monoFont}`;
            ctx.textAlign = "left";
            ctx.fillText(`${label} ${value.toFixed(1)} µm`, PAD.left + 6, textY);
        };
        if (win) {
            level(win.threshold, "detect", [4, 4]);
            level(win.edgeThreshold, "edge", [2, 3]);
        }

        // The +/-45 geofence, the 0 deg laser axis, and - zoomed - the measured peak the
        // mirrored window pivots about, which is the whole thing being judged at this scale.
        const marker = (theta, color, dash) => {
            const x = Math.round(sx(theta)) + 0.5;
            ctx.save();
            ctx.setLineDash(dash);
            ctx.strokeStyle = color;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(x, PAD.top);
            ctx.lineTo(x, PAD.top + plotH);
            ctx.stroke();
            ctx.restore();
        };
        if (!tight) {
            marker(-PROPOSED_WINDOW_DEGREES, tokens.warn, [4, 4]);
            marker(PROPOSED_WINDOW_DEGREES, tokens.warn, [4, 4]);
        }
        marker(0, tokens.brand, [2, 3]);
        if (tight && Number.isFinite(win?.peakTheta)) {
            marker(win.peakTheta, tokens.kept, [6, 3]);
        }

        // P10-P90 spread of the unfixed residuals: the band is what shows whether a feature is a
        // real shift of the whole surface or just a few outliers dragging the mean.
        if (series.original.length > 1) {
            ctx.fillStyle = tokens.keptFill;
            ctx.beginPath();
            series.original.forEach((b, i) => {
                const x = sx(b.theta);
                const y = sy(b.p90);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            for (let i = series.original.length - 1; i >= 0; i--) {
                ctx.lineTo(sx(series.original[i].theta), sy(series.original[i].p10));
            }
            ctx.closePath();
            ctx.fill();
        }

        const line = (bins, color, width_) => {
            if (bins.length < 2) return;
            ctx.strokeStyle = color;
            ctx.lineWidth = width_;
            ctx.beginPath();
            bins.forEach((b, i) => {
                const x = sx(b.theta);
                const y = sy(b.p50);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            ctx.stroke();
        };
        line(series.original, tokens.kept, 1.75);
        line(series.fixed, tokens.reject, 1.75);

        // Point density across theta, as a faint floor strip. A median from 40 points and one
        // from 400,000 look identical on the curve; this is what says which is which - and the
        // arc's own edges are exactly where counts collapse.
        const maxCount = series.original.reduce((m, b) => Math.max(m, b.count), 0);
        if (maxCount > 0) {
            ctx.fillStyle = tokens.muted2;
            ctx.globalAlpha = 0.3;
            const stripH = 18;
            const base = PAD.top + plotH;
            series.original.forEach((b) => {
                const h = (b.count / maxCount) * stripH;
                ctx.fillRect(sx(b.theta) - 1, base - h, 2, h);
            });
            ctx.globalAlpha = 1;
        }

        if (hover) {
            const x = Math.round(sx(hover.theta)) + 0.5;
            ctx.strokeStyle = tokens.text;
            ctx.globalAlpha = 0.4;
            ctx.beginPath();
            ctx.moveTo(x, PAD.top);
            ctx.lineTo(x, PAD.top + plotH);
            ctx.stroke();
            ctx.globalAlpha = 1;
        }

        ctx.fillStyle = tokens.muted;
        ctx.font = `10.5px ${monoFont}`;
        ctx.textAlign = "center";
        ctx.fillText("polar angle from laser axis (deg)", PAD.left + plotW / 2, height - 4);
        ctx.save();
        ctx.translate(12, PAD.top + plotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText("signed residual (µm)  →  toward laser", 0, 0);
        ctx.restore();
    }, [series, yMax, hover, profile, thetaMin, thetaMax, tight]);

    useEffect(() => { draw(); }, [draw]);
    useEffect(() => {
        const onResize = () => draw();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [draw]);

    // Pan/zoom. useChartPanZoom (the shared hook the other Raw Surface charts use) drives a
    // Chart.js instance through chart.scales/chart.update, and this chart is hand-drawn on a raw
    // canvas, so the hook cannot be reused here. The input conventions are copied from it
    // deliberately so the two feel the same:
    //
    //   Ctrl/Cmd + wheel, or a trackpad pinch  -> zoom, anchored on the angle under the cursor
    //   plain wheel / two-finger swipe         -> pan
    //   drag                                   -> pan (free here: unlike the position charts,
    //                                             this one has no drag-to-select-a-range)
    //   Space + drag                           -> pan, so habits from the other charts carry over
    //   double-click                           -> back to the last preset window
    //
    // Only the angle axis moves. Residual stays symmetric about zero and auto-scales to whatever
    // is in the window - the axis is built that way on purpose (an asymmetric residual axis makes
    // a positive bump read as a baseline shift), so letting it be panned would undo that.
    const dragRef = useRef(null);
    const spaceRef = useRef(false);
    const lastPresetRef = useRef("full");
    const viewRef = useRef(view);
    viewRef.current = view;

    useEffect(() => {
        const onKeyDown = (e) => { if (e.code === "Space" && !e.repeat) spaceRef.current = true; };
        const onKeyUp = (e) => { if (e.code === "Space") spaceRef.current = false; };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);

    // Wheel has to be a non-passive native listener: React's onWheel is registered passive, so
    // preventDefault there is ignored and the dialog scrolls behind the chart instead.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !profile) return undefined;

        const onWheel = (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const plotW = rect.width - PAD.left - PAD.right;
            if (plotW <= 0) return;

            setView((v) => {
                const span = v.max - v.min;
                if (e.ctrlKey || e.metaKey) {
                    const frac = Math.min(Math.max((e.clientX - rect.left - PAD.left) / plotW, 0), 1);
                    const anchor = v.min + frac * span;
                    const factor = Math.exp(-e.deltaY * 0.0015);
                    return clampView(anchor - (anchor - v.min) / factor, anchor + (v.max - anchor) / factor);
                }
                // A plain mouse wheel reports only deltaY; treat that lone axis as panning angle,
                // matching the hook's reasoning that the horizontal axis is the one worth
                // navigating by hand.
                const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
                const shift = (delta / plotW) * span;
                return clampView(v.min + shift, v.max + shift);
            });
        };

        // Safari reports a trackpad pinch as its own gesture events rather than a ctrl-wheel.
        // e.scale is cumulative from gesturestart, so the anchor and start span are captured once.
        let pinch = null;
        const onGestureStart = (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const plotW = rect.width - PAD.left - PAD.right;
            const frac = Math.min(Math.max((e.clientX - rect.left - PAD.left) / plotW, 0), 1);
            pinch = { frac, min: viewRef.current.min, max: viewRef.current.max };
        };
        const onGestureChange = (e) => {
            e.preventDefault();
            if (!pinch) return;
            const span = pinch.max - pinch.min;
            const anchor = pinch.min + pinch.frac * span;
            const factor = e.scale || 1;
            setView(clampView(
                anchor - (anchor - pinch.min) / factor,
                anchor + (pinch.max - anchor) / factor,
            ));
        };
        const onGestureEnd = (e) => { e.preventDefault(); pinch = null; };

        canvas.addEventListener("wheel", onWheel, { passive: false });
        canvas.addEventListener("gesturestart", onGestureStart);
        canvas.addEventListener("gesturechange", onGestureChange);
        canvas.addEventListener("gestureend", onGestureEnd);
        return () => {
            canvas.removeEventListener("wheel", onWheel);
            canvas.removeEventListener("gesturestart", onGestureStart);
            canvas.removeEventListener("gesturechange", onGestureChange);
            canvas.removeEventListener("gestureend", onGestureEnd);
        };
    }, [profile, clampView]);

    const onMouseDown = (e) => {
        const canvas = canvasRef.current;
        if (!canvas || !profile) return;
        // Space held is the other charts' pan gesture; here plain drag already pans, so Space
        // simply does not need special handling beyond not breaking it.
        const rect = canvas.getBoundingClientRect();
        dragRef.current = {
            startClientX: e.clientX,
            plotW: rect.width - PAD.left - PAD.right,
            min: view.min,
            max: view.max,
            moved: false,
        };
    };

    const endDrag = () => { dragRef.current = null; };

    useEffect(() => {
        window.addEventListener("mouseup", endDrag);
        return () => window.removeEventListener("mouseup", endDrag);
    }, []);

    const resetView = () => setView(lastPresetRef.current === "zoom"
        ? { min: -THETA_ZOOM, max: THETA_ZOOM }
        : { min: -THETA_FULL, max: THETA_FULL });

    const onMouseMove = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Dragging pans and suppresses the hover readout - chasing a moving cursor value while
        // the axis itself is moving is unreadable.
        const drag = dragRef.current;
        if (drag && drag.plotW > 0) {
            const dxPx = e.clientX - drag.startClientX;
            if (Math.abs(dxPx) > 2) drag.moved = true;
            if (drag.moved) {
                const span = drag.max - drag.min;
                const shift = -(dxPx / drag.plotW) * span;
                setView(clampView(drag.min + shift, drag.max + shift));
                if (hover) setHover(null);
                return;
            }
        }

        if (series.original.length === 0) return;
        const rect = canvas.getBoundingClientRect();
        const plotW = rect.width - PAD.left - PAD.right;
        const theta = thetaMin + ((e.clientX - rect.left - PAD.left) / plotW) * (thetaMax - thetaMin);

        let best = null;
        let bestD = Infinity;
        for (const b of series.original) {
            const d = Math.abs(b.theta - theta);
            if (d < bestD) { bestD = d; best = b; }
        }
        if (!best) return;
        const match = series.fixed.find((b) => b.theta === best.theta) ?? null;
        setHover({ theta: best.theta, original: best, fixed: match });
    };

    const run = () => dispatch(loadAngleProfile({ range }));

    const rangeDisabled = {
        machine: !rawSurface.machineRange,
        motor: !rawSurface.motorRange,
    };

    const Shell = bare
        ? ({ children }) => (
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
                {children}
            </Box>
        )
        : ({ children }) => (
            <Card sx={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.line}` }}>
                <CardContent>{children}</CardContent>
            </Card>
        );

    return (
        <Shell>
            <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: "10px" }}>
                    <Typography sx={cardTitleSx}>Residual vs Angle</Typography>
                    <Tooltip
                        title={
                            <>
                                Every point of every fitted circle, plotted as its signed distance
                                from its own circle against its polar angle, pooled into per-bin
                                medians. 0° is the direction of the laser head; positive residual
                                means closer to the laser.
                                <br /><br />
                                A specular &quot;nipple&quot; can only pull points toward the
                                laser, so it appears as a positive bump near 0°. Its centre and
                                width are what the clipping window should be built from.
                            </>
                        }
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: tokens.muted2 }} />
                    </Tooltip>
                    <Box sx={{ flexGrow: 1 }} />
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={rangeChoice}
                        onChange={(e, v) => v && setRangeChoice(v)}
                    >
                        <ToggleButton value="all" sx={{ fontSize: "11px", py: "2px", px: "9px" }}>
                            Whole capture
                        </ToggleButton>
                        <ToggleButton
                            value="machine"
                            disabled={rangeDisabled.machine}
                            sx={{ fontSize: "11px", py: "2px", px: "9px" }}
                        >
                            Machine
                        </ToggleButton>
                        <ToggleButton
                            value="motor"
                            disabled={rangeDisabled.motor}
                            sx={{ fontSize: "11px", py: "2px", px: "9px" }}
                        >
                            Motor
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={preset}
                        onChange={(e, v) => {
                            if (!v) return;
                            lastPresetRef.current = v;
                            setView(v === "zoom"
                                ? { min: -THETA_ZOOM, max: THETA_ZOOM }
                                : { min: -THETA_FULL, max: THETA_FULL });
                        }}
                    >
                        <ToggleButton value="full" sx={{ fontSize: "11px", py: "2px", px: "9px" }}>
                            ±90°
                        </ToggleButton>
                        <ToggleButton value="zoom" sx={{ fontSize: "11px", py: "2px", px: "9px" }}>
                            ±15°
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={run}
                        disabled={!rawSurface.captureId || status === "loading"}
                        sx={{ fontSize: "11.5px", py: "2px" }}
                    >
                        {profile ? "Re-run" : "Run analysis"}
                    </Button>
                </Stack>

                {!profile && status !== "loading" && (
                    <Typography sx={{ ...labelSx, py: "28px", textAlign: "center", my: bare ? "auto" : 0 }}>
                        {rawSurface.captureId
                            ? "Walks every line in the capture — a few seconds. Run it to measure where the nipple sits."
                            : "Load a capture first."}
                    </Typography>
                )}

                {status === "loading" && (
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}
                        sx={{ py: "28px", my: bare ? "auto" : 0 }}>
                        <CircularProgress size={15} />
                        <Typography sx={labelSx}>Walking every circle in the capture…</Typography>
                    </Stack>
                )}

                {status === "error" && (
                    <Typography sx={{ ...labelSx, color: tokens.reject, py: "10px" }}>
                        {rawSurface.angleProfileError}
                    </Typography>
                )}

                {profile && status !== "loading" && (
                    <>
                        {stale && (
                            <Typography sx={{ ...labelSx, color: tokens.warn, mb: "6px" }}>
                                Circle-fix settings changed since this ran — the “after fixing” curve is from the
                                previous settings. Re-run to update it.
                            </Typography>
                        )}
                        <canvas
                            ref={canvasRef}
                            style={{
                                width: "100%",
                                height: CHART_HEIGHT,
                                display: "block",
                                cursor: "grab",
                            }}
                            onMouseDown={onMouseDown}
                            onMouseMove={onMouseMove}
                            onMouseUp={endDrag}
                            onMouseLeave={() => { endDrag(); setHover(null); }}
                            onDoubleClick={resetView}
                        />
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            alignItems="center"
                            columnGap={2}
                            rowGap={0.5}
                            sx={{ mt: "8px" }}
                        >
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 16, height: 0, borderTop: `2px solid ${tokens.kept}` }} />
                                <Typography sx={labelSx}>Median, original fit</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 16, height: 0, borderTop: `2px solid ${tokens.reject}` }} />
                                <Typography sx={labelSx}>Median, after circle fixing</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 14, height: 8, backgroundColor: tokens.keptFill }} />
                                <Typography sx={labelSx}>P10–P90, original</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{
                                    width: 14, height: 9,
                                    backgroundColor: tokens.rejectFill,
                                    border: `1px solid ${tokens.reject}`,
                                }} />
                                <Typography sx={labelSx}>Measured clip window</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{
                                    width: 14, height: 9,
                                    backgroundColor: tokens.paper,
                                    border: `1px solid ${tokens.muted}`,
                                }} />
                                <Typography sx={labelSx}>Beyond usable arc</Typography>
                            </Stack>
                            {tight ? (
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Box sx={{ width: 16, height: 0, borderTop: `2px dashed ${tokens.kept}` }} />
                                    <Typography sx={labelSx}>Measured peak</Typography>
                                </Stack>
                            ) : (
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Box sx={{ width: 16, height: 0, borderTop: `2px dashed ${tokens.warn}` }} />
                                    <Typography sx={labelSx}>±45° proposed window</Typography>
                                </Stack>
                            )}
                            <Box sx={{ flexGrow: 1 }} />
                            <Typography sx={labelSx}>
                                <span style={numberSx}>{profile.circlesUsed.toLocaleString()}</span> circles,{" "}
                                <span style={numberSx}>{profile.binDegrees}</span>° bins
                            </Typography>
                        </Stack>

                        {profile.arc && profile.arc.high > profile.arc.low && (
                            <Box sx={{
                                mt: "10px", pt: "10px", borderTop: `1px solid ${tokens.line}`,
                                display: "flex", flexWrap: "wrap", alignItems: "center",
                                columnGap: "18px", rowGap: "4px",
                            }}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: tokens.textSoft }}>
                                    Usable arc {profile.arc.low.toFixed(1)}° … {profile.arc.high.toFixed(1)}°
                                    {" ("}{profile.arc.centralAngle.toFixed(1)}° central{")"}
                                </Typography>
                                {/* Which test ended each side is the whole point of measuring two
                                    of them - it says whether this capture ran out of field of view
                                    or ran out of usable incidence, which no single number can. */}
                                <Typography sx={labelSx}>
                                    ends: <span style={numberSx}>{EDGE_REASON[profile.arc.lowReason] ?? "?"}</span>
                                    {" / "}
                                    <span style={numberSx}>{EDGE_REASON[profile.arc.highReason] ?? "?"}</span>
                                </Typography>
                                <Typography sx={labelSx}>
                                    spread limit <span style={numberSx}>{profile.arc.spreadLimit.toFixed(1)}</span> µm
                                    {" (k = "}<span style={numberSx}>{profile.arc.spreadK.toFixed(1)}</span>{")"}
                                </Typography>
                                <Typography sx={labelSx}>
                                    count limit <span style={numberSx}>{Math.round(profile.arc.countLimit).toLocaleString()}</span>
                                </Typography>
                                {/* Compared against the REAL production gate - a minimum total
                                    central angle - not against the proposed ±45° window. Circles
                                    narrower than the measured usable arc are ones whose whole arc
                                    is inside the region the surface still reads well. */}
                                <Typography sx={labelSx}>
                                    vs min central angle{" "}
                                    <span style={numberSx}>{rawSurface.angleLimit}</span>°:{" "}
                                    {profile.arc.centralAngle >= rawSurface.angleLimit
                                        ? `usable arc is ${(profile.arc.centralAngle - rawSurface.angleLimit).toFixed(1)}° wider than the minimum`
                                        : `usable arc is below the minimum — every circle would gate out`}
                                </Typography>
                            </Box>
                        )}

                        {/* The detector's verdict in words, next to the window it drew. "Nothing
                            found" is a real result worth stating plainly - on a diffuse shaft the
                            apex is the best data on the line, so an empty window is the correct
                            answer, not a failed analysis. */}
                        {profile.window && (
                            <Box sx={{
                                mt: "10px", pt: "10px", borderTop: `1px solid ${tokens.line}`,
                                display: "flex", flexWrap: "wrap", alignItems: "center",
                                columnGap: "18px", rowGap: "4px",
                            }}>
                                <Typography
                                    sx={{
                                        fontSize: "12px", fontWeight: 700,
                                        color: profile.window.found ? tokens.reject : tokens.kept,
                                    }}
                                >
                                    {profile.window.found
                                        ? `Clip window ${profile.window.start.toFixed(1)}° … ${profile.window.stop.toFixed(1)}°`
                                        : "No nipple detected — nothing to exclude"}
                                </Typography>
                                <Typography sx={labelSx}>
                                    baseline <span style={numberSx}>{profile.window.baseline.toFixed(1)}</span> µm
                                </Typography>
                                <Typography sx={labelSx}>
                                    scatter <span style={numberSx}>{profile.window.scatter.toFixed(1)}</span> µm
                                </Typography>
                                <Typography sx={labelSx}>
                                    detect at <span style={numberSx}>{profile.window.threshold.toFixed(1)}</span> µm
                                    {" "}(k = <span style={numberSx}>{profile.window.k.toFixed(1)}</span>)
                                </Typography>
                                <Typography sx={labelSx}>
                                    edge at <span style={numberSx}>{profile.window.edgeThreshold.toFixed(1)}</span> µm
                                    {" "}(k = <span style={numberSx}>{profile.window.edgeK.toFixed(1)}</span>)
                                </Typography>
                                {profile.window.found && (
                                    <>
                                        <Typography sx={labelSx}>
                                            peak <span style={numberSx}>{profile.window.peakResidual.toFixed(1)}</span> µm
                                            {" at "}<span style={numberSx}>{profile.window.peakTheta.toFixed(2)}</span>°
                                        </Typography>
                                        {/* Both sides, as measured. The lobe is genuinely skewed -
                                            steep rise, slow decay - so these are expected to
                                            differ; it is not a defect and no longer flagged. */}
                                        <Typography sx={labelSx}>
                                            half-widths{" "}
                                            <span style={numberSx}>{profile.window.halfWidthLow.toFixed(2)}</span>°
                                            {" / "}
                                            <span style={numberSx}>{profile.window.halfWidthHigh.toFixed(2)}</span>°
                                        </Typography>
                                        <Typography sx={labelSx}>
                                            excludes{" "}
                                            <span style={numberSx}>
                                                {profile.window.totalPoints > 0
                                                    ? ((100 * profile.window.excludedPoints) / profile.window.totalPoints).toFixed(1)
                                                    : "0.0"}
                                            </span>% of points
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        {/* Always mounted, hidden until the pointer is over the chart. Rendering
                            it conditionally moved everything below it - and in the dialog, the
                            dialog itself - every time the mouse entered or left the plot. */}
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            columnGap={2.5}
                            rowGap={0.5}
                            aria-hidden={!hover}
                            sx={{ mt: "8px", visibility: hover ? "visible" : "hidden" }}
                        >
                            <Typography sx={labelSx}>
                                θ <span style={numberSx}>{hover ? hover.theta.toFixed(2) : "0.00"}</span>°
                            </Typography>
                            <Typography sx={labelSx}>
                                median <span style={numberSx}>{hover ? hover.original.p50.toFixed(1) : "0.0"}</span> µm
                            </Typography>
                            <Typography sx={labelSx}>
                                P10–P90 <span style={numberSx}>{hover ? hover.original.p10.toFixed(1) : "0.0"}</span> …{" "}
                                <span style={numberSx}>{hover ? hover.original.p90.toFixed(1) : "0.0"}</span> µm
                            </Typography>
                            <Typography sx={labelSx}>
                                after fixing{" "}
                                <span style={numberSx}>
                                    {hover && hover.fixed ? hover.fixed.p50.toFixed(1) : "—"}
                                </span>{" "}
                                µm
                            </Typography>
                            <Typography sx={labelSx}>
                                n <span style={numberSx}>{hover ? hover.original.count.toLocaleString() : "0"}</span>
                            </Typography>
                        </Stack>
                    </>
                )}
            </>
        </Shell>
    );
}

/**
 * Gridline and label spacing for the angle axis, as [step, labelEvery]. Picked from a fixed
 * ladder rather than computed, so that panning never makes the gridlines drift between values -
 * the spacing changes only when the zoom crosses a band.
 */
function angleGrid(span) {
    if (span <= 4) return [0.25, 1];
    if (span <= 10) return [0.5, 2];
    if (span <= 24) return [1, 5];
    if (span <= 60) return [2.5, 10];
    if (span <= 120) return [5, 30];
    return [15, 30];
}

/** 1/2/5 x 10^n step that puts roughly 4-6 labelled gridlines on the residual axis. */
function niceStep(max) {
    const rough = max / 3;
    const power = Math.pow(10, Math.floor(Math.log10(rough)));
    const scaled = rough / power;
    const step = scaled >= 5 ? 5 : scaled >= 2 ? 2 : 1;
    return step * power;
}
